import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AccessDenied, errorStatus } from '@/components/RouteError';
import { Button } from '@/components/ui/button';
import { MovementCard } from '@/components/finance/MovementCard';
import { ReverseDialog } from '@/components/finance/ReverseDialog';
import { apartmentsQuery } from '@/lib/apartments';
import { errorMessage } from '@/lib/errors';
import {
  financeAccountsQuery,
  financeCategoriesQuery,
  financeCounterpartiesQuery,
  financeMovementQuery,
  financeSettingsQuery,
} from '@/lib/finance';
import { usePermissions } from '@/lib/me';

export const Route = createFileRoute('/_app/finance/movements/$id')({
  component: MovementDetailRoute,
});

// The route reads the param; the page takes it as a prop, so the page can be
// mounted in a test without a matched route (same split as the account card).
function MovementDetailRoute() {
  const { id } = Route.useParams();
  return <MovementDetailPage id={id} />;
}

export function MovementDetailPage({ id }: { id: string }) {
  const { can } = usePermissions();
  const [reversing, setReversing] = useState(false);

  const movement = useQuery(financeMovementQuery(id));
  const accounts = useQuery(financeAccountsQuery(true));
  const categories = useQuery(financeCategoriesQuery(true));
  const counterparties = useQuery(financeCounterpartiesQuery(true));
  const apartments = useQuery(apartmentsQuery);
  const settings = useQuery(financeSettingsQuery);

  if (errorStatus(movement.error) === 403) return <AccessDenied />;
  if (movement.isLoading) return <p className="text-sm text-muted">Loading payment…</p>;
  if (movement.isError || !movement.data) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {errorMessage(movement.error)}
      </p>
    );
  }

  const m = movement.data;

  return (
    <div className="flex flex-col gap-4">
      <Link to="/finance/movements" className="text-sm text-primary hover:underline">
        ← All payments
      </Link>

      <MovementCard
        movement={m}
        accounts={accounts.data}
        categories={categories.data}
        counterparties={counterparties.data}
        apartments={apartments.data}
        actions={
          // A movement already reversed cannot be reversed again (409
          // already_reversed), so the button is not offered on one. Neither
          // can a reversal itself: reverse.go answers 422 "a reversal is not
          // reversed; reverse the original's replacement instead" for every
          // movement carrying reverses_id — offering the button there is a
          // reason typed into a form that can only end in that error.
          can('finance.confirm') && !m.reversed_by_id && !m.reverses_id ? (
            <Button variant="destructive" size="sm" onClick={() => setReversing(true)}>
              Reverse
            </Button>
          ) : undefined
        }
      />

      {/* Attachments arrive in slice 2. Nothing is rendered here until they
          do — a heading promising a feature is worse than its absence. */}

      {reversing && (
        <ReverseDialog
          movement={m}
          accounts={(accounts.data ?? []).filter((a) => a.is_active)}
          categories={categories.data ?? []}
          counterparties={counterparties.data}
          apartments={apartments.data}
          businessTz={settings.data?.business_tz}
          canManage={can('finance.manage')}
          onClose={() => setReversing(false)}
        />
      )}
    </div>
  );
}
